import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiEndpoints } from '../api/client';

// Event name for credit updates
export const CREDITS_UPDATED_EVENT = 'credits-updated';

// Helper function to trigger credit refresh from anywhere in the app
export const triggerCreditRefresh = () => {
    window.dispatchEvent(new CustomEvent(CREDITS_UPDATED_EVENT));
};

const CreditDisplay = () => {
    const navigate = useNavigate();
    const [creditData, setCreditData] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchCredits = useCallback(async () => {
        try {
            const data = await apiEndpoints.auth.getCredits();
            setCreditData(data);
        } catch (error) {
            console.error('Error fetching credits:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCredits();

        // Listen for credit update events
        const handleCreditsUpdated = () => {
            fetchCredits();
        };

        window.addEventListener(CREDITS_UPDATED_EVENT, handleCreditsUpdated);

        return () => {
            window.removeEventListener(CREDITS_UPDATED_EVENT, handleCreditsUpdated);
        };
    }, [fetchCredits]);

    const styles = {
        // Container styles
        container: {
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
            padding: 'var(--space-sm) var(--space-lg)',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-full)',
        },
        // Loading state
        loading: {
            padding: 'var(--space-sm) var(--space-md)',
            textAlign: 'center',
            color: 'var(--text-tertiary)',
            fontSize: '0.8rem',
        },
        // Pro badge styles
        proBadge: {
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
            padding: 'var(--space-sm) var(--space-md)',
            background: 'linear-gradient(135deg, var(--amber-500) 0%, var(--amber-400) 100%)',
            borderRadius: 'var(--radius-full)',
            fontWeight: 600,
            color: '#000',
        },
        proIcon: {
            fontSize: '1rem',
        },
        proText: {
            fontSize: '0.85rem',
            fontWeight: 700,
        },
        // Credit icon
        creditIcon: {
            width: '24px',
            height: '24px',
        },
        // Credits info
        creditsInfo: {
            display: 'flex',
            flexDirection: 'column',
            lineHeight: 1.2,
        },
        creditsCount: {
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: '1rem',
            color: 'var(--text-primary)',
        },
        creditsLabel: {
            fontSize: '0.7rem',
            color: 'var(--text-tertiary)',
        },
        // Plan badge
        planBadge: {
            padding: 'var(--space-xs) var(--space-sm)',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.7rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginLeft: 'var(--space-sm)',
        },
        trialBadge: {
            background: 'rgba(245, 158, 11, 0.15)',
            color: 'var(--amber-400)',
        },
        freeBadge: {
            background: 'rgba(99, 102, 241, 0.15)',
            color: 'var(--indigo-400)',
        },
        // Upgrade button
        upgradeBtn: {
            background: 'var(--gradient-primary)',
            color: 'var(--text-primary)',
            padding: 'var(--space-xs) var(--space-md)',
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '0.75rem',
            marginLeft: 'var(--space-sm)',
            transition: 'transform var(--duration-fast), box-shadow var(--duration-fast)',
        },
    };

    if (loading) {
        return <div style={styles.loading}>Loading...</div>;
    }

    if (!creditData) return null;

    const { tier, credits, is_premium } = creditData;

    // Pro users: show badge
    if (is_premium) {
        return (
            <div style={styles.proBadge}>
                <span style={styles.proIcon}>✨</span>
                <span style={styles.proText}>Pro</span>
            </div>
        );
    }

    // Calculate total credits remaining
    const creditItems = [
        { key: 'ai_resumes', max: 5 },
        { key: 'ai_copilot', max: 5 },
        { key: 'mock_interviews', max: 3 },
        { key: 'star_stories', max: 3 },
        { key: 'company_research', max: 3 },
        { key: 'resume_walkthroughs', max: 3 },
    ];

    const totalRemaining = creditItems.reduce((sum, item) => sum + (credits[item.key] || 0), 0);

    // Free or trial users: show compact credit display (clickable to upgrade)
    return (
        <div
            style={{
                ...styles.container,
                cursor: 'pointer',
                transition: 'transform var(--duration-fast), box-shadow var(--duration-fast)',
            }}
            onClick={() => navigate('/upgrade')}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = 'var(--glow-indigo)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
            }}
            title="Click to upgrade"
        >
            {/* Credit Icon */}
            <div style={styles.creditIcon}>
                <svg viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="url(#credit-icon-grad)" strokeWidth="2"/>
                    <path d="M12 6v12M6 12h12" stroke="url(#credit-icon-grad)" strokeWidth="2" strokeLinecap="round"/>
                    <defs>
                        <linearGradient id="credit-icon-grad" x1="2" y1="2" x2="22" y2="22">
                            <stop offset="0%" stopColor="var(--indigo-500)"/>
                            <stop offset="100%" stopColor="var(--cyan-400)"/>
                        </linearGradient>
                    </defs>
                </svg>
            </div>

            {/* Credits Info */}
            <div style={styles.creditsInfo}>
                <span style={styles.creditsCount}>{totalRemaining}</span>
                <span style={styles.creditsLabel}>Credits</span>
            </div>

            {/* Plan Badge - always show upgrade indicator */}
            {tier === 'trial' ? (
                <span style={{ ...styles.planBadge, ...styles.trialBadge }}>Trial</span>
            ) : (
                <span style={{
                    ...styles.planBadge,
                    ...styles.freeBadge,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                }}>
                    <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '12px', height: '12px' }}>
                        <path d="M8 1l2 5h5l-4 3 1.5 5L8 11l-4.5 3L5 9 1 6h5l2-5z"/>
                    </svg>
                    Upgrade
                </span>
            )}
        </div>
    );
};

export default CreditDisplay;
